<template>
  <section class="finding-band">
    <div class="finding-band-head">
      <div>
        <div class="finding-section-label">{{ meta.title }}</div>
        <p v-if="meta.desc" class="finding-band-desc">{{ meta.desc }}</p>
      </div>
      <span class="finding-band-badge">{{ meta.badge }}</span>
    </div>

    <!-- COMPARISON_PAGE_GAP -->
    <div v-if="code === 'COMPARISON_PAGE_GAP'" class="finding-band-body">
      <div v-if="finding.comparison_query_type" class="finding-field">
        <div class="finding-field-label">Query type</div>
        <span class="chip chip-info">{{ normalizeLabel(finding.comparison_query_type) }}</span>
      </div>
      <div v-if="asList(finding.recommended_comparison_targets).length" class="finding-field">
        <div class="finding-field-label">Recommended comparison targets</div>
        <div class="chip-row">
          <span
            v-for="target in asList(finding.recommended_comparison_targets)"
            :key="target"
            class="chip chip-positive"
          >{{ displayValue(target) }}</span>
        </div>
      </div>
      <p v-if="asList(finding.competing_comparison_pages).length === 0" class="finding-note">
        No competitor comparison pages were found — this is a market-wide gap, not just a brand gap.
      </p>
      <DiagnosisFieldBlock v-else kind="urls" :items="finding.competing_comparison_pages" label="Competing comparison pages" />
    </div>

    <!-- CONTENT_GAP -->
    <div v-else-if="code === 'CONTENT_GAP'" class="finding-band-body">
      <div v-if="finding.target_intent" class="intent-box">
        <div class="finding-field-label">Target intent</div>
        <p>{{ finding.target_intent }}</p>
      </div>
      <div v-if="finding.recommended_page_type" class="finding-field">
        <div class="finding-field-label">Recommended format</div>
        <span class="chip chip-info">{{ normalizeLabel(finding.recommended_page_type) }}</span>
      </div>
      <DiagnosisFieldBlock kind="chips" label="Missing content types" :items="finding.missing_content_types" tone="negative" />
      <DiagnosisFieldBlock kind="urls" label="Competitor examples covering this" :items="finding.competitor_content_examples" boxed />
    </div>

    <!-- KEYWORD_GAP -->
    <div v-else-if="code === 'KEYWORD_GAP'" class="finding-band-body">
      <div v-if="finding.brand_keyword_coverage" class="finding-field">
        <div class="finding-field-label">Brand keyword coverage</div>
        <span class="chip chip-warn">{{ normalizeLabel(finding.brand_keyword_coverage) }}</span>
      </div>
      <DiagnosisFieldBlock kind="chips" label="Missing keywords" :items="finding.missing_keywords" tone="negative" />
      <DiagnosisFieldBlock kind="chips" label="Recommended keywords" :items="finding.recommended_keywords" tone="positive" />
      <DiagnosisFieldBlock kind="chips" label="Competitor keyword advantage" :items="finding.competitor_keyword_advantage" tone="neutral" />
    </div>

    <!-- FAQ_GAP -->
    <div v-else-if="code === 'FAQ_GAP'" class="finding-band-body">
      <div class="chip-row mb-3">
        <span class="chip chip-neutral">FAQ section: {{ yesNo(finding.faq_section_present) || 'unknown' }}</span>
        <span class="chip chip-neutral">FAQ schema: {{ yesNo(finding.faq_schema_present) || 'unknown' }}</span>
        <span v-if="finding.recommended_faq_format" class="chip chip-info">{{ finding.recommended_faq_format }}</span>
      </div>
      <a v-if="isHttpUrl(finding.faq_page_url)" :href="finding.faq_page_url" target="_blank" rel="noopener" class="finding-url">{{ finding.faq_page_url }}</a>
      <div v-if="asList(finding.missing_faq_questions).length" class="finding-field">
        <div class="finding-field-label">Questions users are asking that go unanswered</div>
        <div class="q-list">
          <div v-for="(question, i) in asList(finding.missing_faq_questions)" :key="i" class="q-row">
            Q — {{ displayValue(question) }}
          </div>
        </div>
      </div>
      <DiagnosisFieldBlock kind="chips" label="Existing FAQ questions" :items="finding.existing_faq_questions" tone="neutral" />
      <DiagnosisFieldBlock kind="urls" label="Competitor FAQ pages" :items="finding.competitor_faq_pages" boxed />
    </div>

    <!-- SOURCE_INFLUENCE_GAP_PROVIDER -->
    <div v-else-if="code === 'SOURCE_INFLUENCE_GAP_PROVIDER'" class="finding-band-body">
      <div class="finding-field">
        <div class="finding-field-label">Provider named in answer</div>
        <span :class="['chip', finding.provider_named_in_answer ? 'chip-positive' : 'chip-negative']">
          {{ finding.provider_name ? `${finding.provider_name}: ` : '' }}{{ yesNo(finding.provider_named_in_answer) || 'unknown' }}
        </span>
      </div>
      <DiagnosisFieldBlock kind="chips" label="Competitor providers named instead" :items="finding.competitor_providers_named" tone="warn" />
      <DiagnosisFieldBlock kind="urls" label="Sources surfacing them" :items="finding.sources_surfacing_competitor_providers" boxed />
      <p v-if="finding.influence_gap_summary" class="finding-note">{{ finding.influence_gap_summary }}</p>
    </div>

    <!-- THIRD_PARTY_COVERAGE_GAP_PROVIDER -->
    <div v-else-if="code === 'THIRD_PARTY_COVERAGE_GAP_PROVIDER'" class="finding-band-body">
      <div v-if="finding.provider_name" class="provider-name">{{ finding.provider_name }}</div>
      <div class="chip-row mb-3">
        <span v-if="finding.profile_completeness" class="chip chip-negative">
          Profile · {{ normalizeLabel(finding.profile_completeness) }}
        </span>
        <span v-if="finding.coverage_gap_severity" class="chip chip-warn">
          {{ normalizeLabel(finding.coverage_gap_severity) }} gap
        </span>
      </div>
      <DiagnosisFieldBlock kind="chips" label="Directories checked" :items="finding.directories_checked" tone="neutral" />
      <div class="two-col">
        <div>
          <div class="finding-field-label">Present on</div>
          <DiagnosisFieldBlock kind="chips" :items="finding.directories_present" tone="positive" />
        </div>
        <div>
          <div class="finding-field-label">Missing from</div>
          <DiagnosisFieldBlock kind="chips" :items="finding.directories_missing" tone="negative" />
        </div>
      </div>
      <p v-if="finding.competitor_directory_advantage" class="finding-note">{{ finding.competitor_directory_advantage }}</p>
    </div>

    <!-- PERSONA_ALIGNMENT_GAP -->
    <div v-else-if="code === 'PERSONA_ALIGNMENT_GAP'" class="finding-band-body">
      <div class="persona-card">
        <div class="finding-field-label">Persona{{ finding.persona_name ? ` · ${finding.persona_name}` : '' }}</div>
        <p v-if="finding.persona_motivation">{{ finding.persona_motivation }}</p>
        <div class="chip-row">
          <span :class="['chip', finding.content_matches_persona_intent ? 'chip-positive' : 'chip-negative']">
            Matches intent: {{ yesNo(finding.content_matches_persona_intent) || 'unknown' }}
          </span>
          <span :class="['chip', finding.content_matches_persona_language ? 'chip-positive' : 'chip-negative']">
            Matches language: {{ yesNo(finding.content_matches_persona_language) || 'unknown' }}
          </span>
        </div>
      </div>
      <DiagnosisFieldBlock kind="bullets" label="Misalignment examples" :items="finding.misalignment_examples" />
      <DiagnosisFieldBlock kind="bullets" label="Recommended adjustments" :items="finding.recommended_content_adjustments" />
      <DiagnosisFieldBlock kind="urls" label="Recommended target pages" :items="finding.recommended_target_pages" />
      <DiagnosisFieldBlock kind="chips" label="Competitor persona alignment" :items="finding.competitor_persona_alignment" tone="neutral" />
    </div>

    <!-- SCHEMA_GAP -->
    <div v-else-if="code === 'SCHEMA_GAP'" class="finding-band-body">
      <DiagnosisSchemaByPage :finding="finding" />
    </div>

    <!-- VISIBILITY_GAP -->
    <div v-else-if="code === 'VISIBILITY_GAP'" class="finding-band-body">
      <div v-if="finding.visibility_gap_type" class="finding-field">
        <div class="finding-field-label">Gap type</div>
        <span class="chip chip-info">{{ normalizeLabel(finding.visibility_gap_type) }}</span>
      </div>
      <div class="stat-row">
        <div class="stat-box">
          <div class="stat-value">{{ citationCount(finding.brand_cited_urls, finding.brand_cited_count) }}</div>
          <div class="stat-label">Brand citations</div>
        </div>
        <div class="stat-box">
          <div class="stat-value">{{ citationCount(finding.competitor_cited_urls, finding.competitor_cited_count) }}</div>
          <div class="stat-label">Competitor citations</div>
        </div>
      </div>
      <DiagnosisFieldBlock kind="urls" label="Brand cited URLs" :items="finding.brand_cited_urls" boxed />
      <DiagnosisFieldBlock kind="urls" label="Competitor cited URLs" :items="finding.competitor_cited_urls" boxed />
      <DiagnosisFieldBlock kind="urls" label="Recommended citation targets" :items="finding.recommended_citation_targets" boxed />
    </div>

    <!-- BRAND_VOICE_GAP -->
    <div v-else-if="code === 'BRAND_VOICE_GAP'" class="finding-band-body">
      <div v-if="finding.ai_category_language" class="intent-box">
        <div class="finding-field-label">AI category language</div>
        <p>{{ finding.ai_category_language }}</p>
      </div>
      <div v-if="finding.brand_current_voice" class="finding-field">
        <div class="finding-field-label">Brand current voice</div>
        <p>{{ finding.brand_current_voice }}</p>
      </div>
      <DiagnosisFieldBlock kind="bullets" label="Voice mismatch examples" :items="finding.voice_mismatch_examples" />
      <DiagnosisFieldBlock kind="chips" label="Recommended tone adjustments" :items="finding.recommended_tone_adjustments" tone="positive" />
    </div>

    <!-- TRUST_SIGNAL_GAP -->
    <div v-else-if="code === 'TRUST_SIGNAL_GAP'" class="finding-band-body">
      <div v-if="finding.trust_signal_primary_gap" class="finding-field">
        <div class="finding-field-label">Primary gap</div>
        <span class="chip chip-info">{{ normalizeLabel(finding.trust_signal_primary_gap) }}</span>
      </div>
      <div class="two-col">
        <div>
          <DiagnosisFieldBlock kind="chips" label="Own-site present" :items="finding.own_site_trust_signals_present" tone="positive" />
          <DiagnosisFieldBlock kind="chips" label="Own-site missing" :items="finding.own_site_trust_signals_missing" tone="negative" />
        </div>
        <div>
          <DiagnosisFieldBlock kind="chips" label="Third-party present" :items="finding.third_party_trust_signals_present" tone="positive" />
          <DiagnosisFieldBlock kind="chips" label="Third-party missing" :items="finding.third_party_trust_signals_missing" tone="negative" />
        </div>
      </div>
      <DiagnosisFieldBlock kind="chips" label="High-influence platforms missing" :items="finding.high_influence_platforms_missing" tone="negative" />
      <DiagnosisFieldBlock kind="chips" label="Third-party platforms checked" :items="finding.third_party_platforms_checked" tone="neutral" />
      <p v-if="finding.competitor_trust_advantage" class="finding-note">{{ finding.competitor_trust_advantage }}</p>
    </div>

    <!-- TECHNICAL_BLOCKER -->
    <div v-else-if="code === 'TECHNICAL_BLOCKER'" class="finding-band-body">
      <div class="chip-row mb-3">
        <span v-if="finding.blocker_type" class="chip chip-info">{{ normalizeLabel(finding.blocker_type) }}</span>
        <span v-if="finding.blocker_severity" class="chip chip-warn">{{ normalizeLabel(finding.blocker_severity) }}</span>
      </div>
      <div v-if="finding.technical_details" class="mono-box">{{ finding.technical_details }}</div>
      <DiagnosisFieldBlock
        kind="urls"
        :label="`Affected URLs (${asList(finding.affected_urls).length})`"
        :items="finding.affected_urls"
        boxed
      />
    </div>

    <!-- RETRIEVAL_GAP -->
    <div v-else-if="code === 'RETRIEVAL_GAP'" class="finding-band-body">
      <DiagnosisFieldBlock kind="chips" label="Retrieval blockers" :items="finding.retrieval_blockers" tone="negative" />
      <DiagnosisFieldBlock kind="chips" label="Content format issues" :items="finding.content_format_issues" tone="warn" />
      <DiagnosisFieldBlock kind="urls" label="Affected URLs" :items="finding.affected_urls" boxed />
    </div>

    <!-- THIRD_PARTY_COVERAGE_GAP -->
    <div v-else-if="code === 'THIRD_PARTY_COVERAGE_GAP'" class="finding-band-body">
      <div class="chip-row mb-3">
        <span v-if="finding.coverage_gap_severity" class="chip chip-warn">
          {{ normalizeLabel(finding.coverage_gap_severity) }} coverage gap
        </span>
        <span v-if="absentSourceCount" class="chip chip-negative">
          {{ absentSourceCount }} of {{ asList(finding.all_cited_sources).length || asList(finding.brand_presence_per_source).length }} cited sources omit the brand
        </span>
      </div>
      <DiagnosisFieldBlock kind="objects" label="Brand presence per source" :items="finding.brand_presence_per_source" />
      <DiagnosisFieldBlock kind="objects" label="All cited sources" :items="finding.all_cited_sources" />
      <DiagnosisFieldBlock kind="chips" label="Recommended coverage types" :items="finding.recommended_coverage_types" tone="positive" />
    </div>

    <!-- SOURCE_INFLUENCE_GAP -->
    <div v-else-if="code === 'SOURCE_INFLUENCE_GAP'" class="finding-band-body">
      <div v-if="finding.most_cited_competitor" class="finding-field">
        <div class="finding-field-label">Most-cited competitor</div>
        <span class="chip chip-warn">{{ finding.most_cited_competitor }}</span>
      </div>
      <p v-if="finding.influence_weighting_summary" class="finding-note">{{ finding.influence_weighting_summary }}</p>
      <DiagnosisFieldBlock kind="objects" label="Top influencing sources" :items="finding.top_influencing_sources" />
      <DiagnosisFieldBlock kind="urls" label="High-authority sources without brand" :items="finding.high_authority_sources_without_brand" boxed />
      <DiagnosisFieldBlock kind="objects" label="Competitor citation profiles" :items="finding.competitor_citation_profiles" />
    </div>

    <!-- TRUST_SIGNAL_GAP_PROVIDER -->
    <div v-else-if="code === 'TRUST_SIGNAL_GAP_PROVIDER'" class="finding-band-body">
      <div v-if="finding.provider_name" class="provider-name">{{ finding.provider_name }}</div>
      <div class="chip-row mb-3">
        <span v-if="finding.bio_quality" class="chip chip-info">Bio · {{ normalizeLabel(finding.bio_quality) }}</span>
        <span :class="['chip', finding.bio_present_on_brand_site ? 'chip-positive' : 'chip-negative']">
          On-site bio: {{ yesNo(finding.bio_present_on_brand_site) || 'unknown' }}
        </span>
      </div>
      <a v-if="isHttpUrl(finding.bio_url)" :href="finding.bio_url" target="_blank" rel="noopener" class="finding-url">{{ finding.bio_url }}</a>
      <DiagnosisFieldBlock kind="chips" label="Credentials found" :items="finding.credentials_found" tone="positive" />
      <DiagnosisFieldBlock kind="chips" label="Recognitions found" :items="finding.recognitions_found" tone="positive" />
      <DiagnosisFieldBlock kind="bullets" label="Recommended improvements" :items="finding.recommended_improvements" />
      <p v-if="finding.competitor_trust_advantage" class="finding-note">{{ finding.competitor_trust_advantage }}</p>
    </div>

    <!-- EVIDENCE_INSUFFICIENT -->
    <div v-else-if="code === 'EVIDENCE_INSUFFICIENT'" class="finding-band-body">
      <div v-if="finding.what_was_unclear" class="intent-box">
        <div class="finding-field-label">What's unclear</div>
        <p>{{ finding.what_was_unclear }}</p>
      </div>
      <DiagnosisFieldBlock kind="bullets" label="Suggested investigation" :items="finding.suggested_investigation" />
      <DiagnosisFieldBlock kind="urls" label="Attempted evidence sources" :items="finding.attempted_evidence_sources" boxed />
    </div>

    <!-- Generic fallback -->
    <div v-else class="finding-band-body">
      <template v-for="[key, value] in remainingFields" :key="key">
        <DiagnosisFieldBlock
          v-if="asList(value).length && asList(value).every((item) => isHttpUrl(item) || item?.url)"
          kind="urls"
          :label="humanizeKey(key)"
          :items="value"
          boxed
        />
        <DiagnosisFieldBlock
          v-else-if="isObjectArray(value) || (value && typeof value === 'object' && !Array.isArray(value))"
          kind="objects"
          :label="humanizeKey(key)"
          :items="Array.isArray(value) ? value : [value]"
        />
        <DiagnosisFieldBlock
          v-else-if="Array.isArray(value)"
          kind="chips"
          :label="humanizeKey(key)"
          :items="value"
          :tone="isRecommendedFieldKey(key) ? 'positive' : 'neutral'"
        />
        <div v-else-if="typeof value === 'boolean'" class="finding-field">
          <div class="finding-field-label">{{ humanizeKey(key) }}</div>
          <span :class="['chip', value ? 'chip-positive' : 'chip-negative']">{{ value ? 'yes' : 'no' }}</span>
        </div>
        <div v-else class="finding-field">
          <div class="finding-field-label">{{ humanizeKey(key) }}</div>
          <p>{{ typeof value === 'string' ? value : JSON.stringify(value) }}</p>
        </div>
      </template>
    </div>
  </section>
</template>

<script>
import DiagnosisFieldBlock from './DiagnosisFieldBlock.vue'
import DiagnosisSchemaByPage from './DiagnosisSchemaByPage.vue'
import {
  asList,
  bandMetaFor,
  humanizeKey,
  isHttpUrl,
  isRecommendedFieldKey,
  normalizeLabel,
  remainingTypeFields,
  yesNo
} from '@/utils/diagnosisFindings'

export default {
  name: 'DiagnosisGapTypeBand',
  components: { DiagnosisFieldBlock, DiagnosisSchemaByPage },
  props: {
    finding: {
      type: Object,
      required: true
    }
  },
  computed: {
    code() {
      return String(this.finding?.code || '').toUpperCase()
    },
    meta() {
      return bandMetaFor(this.code)
    },
    absentSourceCount() {
      const presence = asList(this.finding.brand_presence_per_source)
      if (!presence.length) return 0
      return presence.filter((item) => String(item?.status || '').toLowerCase() === 'not_present').length
    },
    remainingFields() {
      return remainingTypeFields(this.finding)
    }
  },
  methods: {
    asList,
    isHttpUrl,
    isRecommendedFieldKey,
    humanizeKey,
    normalizeLabel,
    yesNo,
    displayValue(item) {
      if (item && typeof item === 'object') return item.title || item.url || item.name || JSON.stringify(item)
      return String(item)
    },
    citationCount(urls, explicit) {
      if (explicit != null && explicit !== '') return Number(explicit)
      return asList(urls).length
    },
    isObjectArray(value) {
      return Array.isArray(value) && value.some((item) => item && typeof item === 'object' && !Array.isArray(item))
    }
  }
}
</script>

<style>
.finding-band {
  background: #fff;
  border: 1px solid var(--brand-ocean-100);
  border-radius: 14px;
  padding: 18px 20px 20px;
}

.finding-band-head {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 16px;
}

.finding-section-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--brand-ocean-600);
}

.finding-band-desc {
  margin: 6px 0 0;
  font-size: 13px;
  color: var(--brand-ink-800);
  line-height: 1.45;
}

.finding-band-badge {
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--brand-ocean-600);
  background: var(--brand-ocean-100);
  border-radius: 999px;
  padding: 4px 10px;
}

.finding-band-body {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.finding-field-label {
  font-size: 11px;
  font-weight: 700;
  color: var(--brand-ink-800);
  margin-bottom: 6px;
}

.finding-note {
  margin: 0;
  font-size: 13px;
  color: var(--brand-ink-800);
  line-height: 1.5;
}

.chip-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.chip {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
}

.chip-mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11px;
}

.chip-info {
  background: var(--brand-ocean-100);
  color: var(--brand-ocean-600);
}

.chip-positive {
  background: var(--brand-teal-100);
  color: #127a6a;
}

.chip-negative {
  background: var(--brand-weak-100);
  color: #9b2c2c;
}

.chip-warn {
  background: var(--brand-amber-100);
  color: #92610a;
}

.chip-neutral {
  background: var(--brand-sand-100);
  color: var(--brand-ink-800);
}

.intent-box,
.persona-card,
.mono-box,
.url-box,
.object-card,
.q-row {
  border: 1px solid var(--brand-sand-300);
  border-radius: 10px;
  background: var(--brand-sand-50);
  padding: 12px 14px;
}

.intent-box p,
.persona-card p {
  margin: 0;
  font-size: 13.5px;
  line-height: 1.5;
  color: var(--brand-ink-950);
}

.mono-box {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
  white-space: pre-wrap;
}

.two-col {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.stat-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.stat-box {
  border: 1px solid var(--brand-sand-300);
  border-radius: 10px;
  padding: 12px;
  text-align: center;
}

.stat-value {
  font-size: 28px;
  font-weight: 700;
  color: var(--brand-ink-950);
}

.stat-label {
  font-size: 11px;
  color: var(--brand-ink-800);
}

.finding-url {
  color: var(--brand-ocean-600);
  font-size: 12.5px;
  word-break: break-all;
}

.url-stack {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.q-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.finding-bullets {
  margin: 0;
  padding-left: 18px;
  font-size: 13px;
  color: var(--brand-ink-800);
  line-height: 1.5;
}

.provider-name {
  font-weight: 700;
  font-size: 15px;
  color: var(--brand-ink-950);
}

.object-row {
  display: flex;
  gap: 10px;
  font-size: 12.5px;
  margin-bottom: 4px;
}

.object-key {
  font-weight: 600;
  color: var(--brand-ink-800);
  min-width: 120px;
  text-transform: capitalize;
}

@media (max-width: 800px) {
  .two-col,
  .stat-row {
    grid-template-columns: 1fr;
  }
}
</style>
